import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from 'react'
import useUser from '../hooks/useUser'

export interface Business {
    id: number
    name: string
}

export interface Tenant {
    id: number
    esKey: string
    businesses: Business[]
}

interface TenantBusinessContextType {
    selectedTenant: Tenant | null
    selectedBusiness: Business | null
    setSelectedTenant: (tenant: Tenant | null) => void
    setSelectedBusiness: (business: Business | null) => void
    isLoading: boolean
}

const TenantBusinessContext = createContext<
    TenantBusinessContextType | undefined
>(undefined)

export const TenantBusinessProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { currentUser } = useUser()
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(
        null
    )
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (currentUser) {
            const allBusinesses = currentUser.tenant.brands.flatMap((brand) =>
                brand.businesses.map((business) => ({
                    id: business.id,
                    name: business.name,
                }))
            )

            const tenant: Tenant = {
                id: currentUser.tenantId,
                esKey: currentUser.tenant.esKey,
                businesses: allBusinesses,
            }
            setSelectedTenant(tenant)

            if (allBusinesses.length > 0) {
                setSelectedBusiness(allBusinesses[0])
            }

            setIsLoading(false)
        } else {
            setIsLoading(false)
        }
    }, [currentUser])

    const handleSetSelectedTenant = (tenant: Tenant | null) => {
        setSelectedTenant(tenant)
        if (!tenant) {
            setSelectedBusiness(null)
        }
    }

    const handleSetSelectedBusiness = (business: Business | null) => {
        setSelectedBusiness(business)
    }

    return (
        <TenantBusinessContext.Provider
            value={{
                selectedTenant,
                selectedBusiness,
                setSelectedTenant: handleSetSelectedTenant,
                setSelectedBusiness: handleSetSelectedBusiness,
                isLoading,
            }}
        >
            {children}
        </TenantBusinessContext.Provider>
    )
}

export const useTenantBusiness = () => {
    const context = useContext(TenantBusinessContext)
    if (!context) {
        throw new Error(
            'useTenantBusiness must be used within TenantBusinessProvider'
        )
    }
    return context
}
